import React, { useCallback, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Destination } from '../../../models/tenant';
import { CountryCode } from '../../../models/country';
import CountChip from './CountChip';
import { CountryAutocomplete } from '../../inputs';
import DestinationRow from './DestinationRow';
import ExclusionRow from './ExclusionRow';

const DEFAULT_DESTINATION: Destination = { country: CountryCode.DE, codes: [''], score: 0 };

const ACCORDION_SUMMARY_SX = {
  backgroundColor: 'background.grey',
  borderRadius: '8px 8px 0 0',
  '&.Mui-expanded': { minHeight: 56 },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0',
    '&.Mui-expanded': { margin: '12px 0' },
  },
};

const OUTLINED_BUTTON_SX = {
  mt: 2,
  color: 'text.darkgrey',
  borderColor: 'border.default',
  '&:hover': {
    backgroundColor: 'background.shadowBlack',
    color: 'text.white',
    borderColor: 'background.shadowBlack',
  },
};

const deleteIconSx = (canDelete: boolean) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: '50%',
  cursor: canDelete ? 'pointer' : 'not-allowed',
  color: canDelete ? 'error.main' : 'text.light',
  '&:hover': canDelete ? { backgroundColor: 'background.shadowBlack' } : {},
});

export type DestinationField = 'preferredDestinations' | 'returnDestinations';

export interface DestinationsTabProps {
  preferredDestinations: Record<string, Destination[]>;
  returnDestinations: Record<string, Destination[]>;
  destinationsToExclude: Destination[];
  onPreferredDestinationsChange: (destinations: Record<string, Destination[]>) => void;
  onReturnDestinationsChange: (destinations: Record<string, Destination[]>) => void;
  onDestinationsToExcludeChange: (destinations: Destination[]) => void;
}

const DestinationsTab: React.FC<DestinationsTabProps> = ({
  preferredDestinations,
  returnDestinations,
  destinationsToExclude,
  onPreferredDestinationsChange,
  onReturnDestinationsChange,
  onDestinationsToExcludeChange,
}) => {
  const { t } = useTranslation();
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>({});

  const handleAccordionChange = useCallback((id: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions(prev => ({ ...prev, [id]: isExpanded }));
  }, []);

  const getData = (field: DestinationField) => field === 'preferredDestinations' ? preferredDestinations : returnDestinations;

  const dispatch = (field: DestinationField, updated: Record<string, Destination[]>) => {
    if (field === 'preferredDestinations') onPreferredDestinationsChange(updated);
    else onReturnDestinationsChange(updated);
  };

  const handleAddGroup = (field: DestinationField) => {
    dispatch(field, { ...getData(field), '': [{ ...DEFAULT_DESTINATION }] });
  };

  const handleRemoveGroup = (field: DestinationField, key: string) => {
    const updated = { ...getData(field) };
    delete updated[key];
    dispatch(field, updated);
  };

  const handleRenameGroupKey = (field: DestinationField, oldKey: string, newKey: string) => {
    const data = getData(field);
    if (oldKey === newKey || data[newKey] !== undefined) return;
    dispatch(field, renameKey(data, oldKey, newKey));
  };

  const handleAddDestinationToGroup = (field: DestinationField, key: string) => {
    const data = getData(field);
    dispatch(field, { ...data, [key]: [...data[key], { ...DEFAULT_DESTINATION }] });
  };

  const handleUpdateDestinationInGroup = (field: DestinationField, key: string, index: number, patch: Partial<Destination>) => {
    const data = getData(field);
    const updatedList = data[key].map((d, i) => (i === index ? { ...d, ...patch } : d));
    dispatch(field, { ...data, [key]: updatedList });
  };

  const handleRemoveDestinationFromGroup = (field: DestinationField, key: string, index: number) => {
    const data = getData(field);
    const filtered = data[key].filter((_, i) => i !== index);
    if (filtered.length === 0) {
      const updated = { ...data };
      delete updated[key];
      dispatch(field, updated);
    } else {
      dispatch(field, { ...data, [key]: filtered });
    }
  };

  const handleAddExclusion = () => {
    onDestinationsToExcludeChange([...destinationsToExclude, { country: CountryCode.DE, codes: [''] }]);
  };

  const handleRemoveExclusion = (index: number) => {
    onDestinationsToExcludeChange(destinationsToExclude.filter((_, i) => i !== index));
  };

  const handleUpdateExclusion = (index: number, patch: Partial<Destination>) => {
    onDestinationsToExcludeChange(destinationsToExclude.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const renderGroupedSection = (
    field: DestinationField,
    title: string,
    description: string,
    chipColor: string,
    accordionPrefix: string
  ) => {
    const data = getData(field);
    const groupCount = Object.keys(data).length;

    return (
      <Accordion defaultExpanded sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
            <CountChip count={groupCount} label="destination" color={chipColor} />
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 2 }}>
          {expandedAccordions[accordionPrefix] !== false && (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
                dangerouslySetInnerHTML={{ __html: description }}
              />

              {Object.entries(data).map(([key, destList], groupIndex) => {
                const { country, postalCode } = parseGroupKey(key);
                const groupAccordionId = `${accordionPrefix}-${groupIndex}`;
                const canDelete = groupCount > 1;

                return (
                  <Accordion
                    key={groupIndex}
                    expanded={expandedAccordions[groupAccordionId] ?? false}
                    onChange={handleAccordionChange(groupAccordionId)}
                    sx={{ mb: 2, border: '1px solid border.default', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ backgroundColor: 'background.white', '&:hover': { backgroundColor: 'background.grey' } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                        <Box sx={{ minWidth: 250 }}>
                          <CountryAutocomplete
                            value={country as CountryCode || null}
                            onChange={(code) => handleRenameGroupKey(field, key, buildGroupKey(code, postalCode))}
                            onClick={(e) => e.stopPropagation()}
                            fullWidth={false}
                            size="small"
                          />
                        </Box>
                        <TextField
                          label={t('Postal Code')}
                          placeholder={t('e.g. 69 / blank')}
                          value={postalCode}
                          onChange={(e) => handleRenameGroupKey(field, key, buildGroupKey(country, e.target.value.trim()))}
                          sx={{ minWidth: 150 }}
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <CountChip count={destList.length} label="destination" />
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title={t('Remove route')}>
                          <Box
                            component="span"
                            onClick={(e) => { e.stopPropagation(); if (canDelete) handleRemoveGroup(field, key); }}
                            sx={deleteIconSx(canDelete)}
                          >
                            <DeleteIcon fontSize="small" />
                          </Box>
                        </Tooltip>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      {expandedAccordions[groupAccordionId] && (
                        <>
                          {destList.map((destination, destIndex) => (
                            <DestinationRow
                              key={destIndex}
                              destination={destination}
                              onCountryChange={(code) => handleUpdateDestinationInGroup(field, key, destIndex, { country: code })}
                              onCodesChange={(codes) => handleUpdateDestinationInGroup(field, key, destIndex, { codes })}
                              onScoreChange={(score) => handleUpdateDestinationInGroup(field, key, destIndex, { score })}
                              onRemove={() => handleRemoveDestinationFromGroup(field, key, destIndex)}
                            />
                          ))}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            disableRipple
                            onClick={() => handleAddDestinationToGroup(field, key)}
                            sx={OUTLINED_BUTTON_SX}
                          >
                            {t('Add Destination')}
                          </Button>
                        </>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}

              <Button variant="outlined" startIcon={<AddIcon />} disableRipple onClick={() => handleAddGroup(field)} sx={OUTLINED_BUTTON_SX}>
                {t('Add New Route')}
              </Button>
            </>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  // Renames a key while preserving insertion order
  function renameKey<T>(record: Record<string, T>, oldKey: string, newKey: string): Record<string, T> {
    const result: Record<string, T> = {};
    for (const [k, v] of Object.entries(record)) {
      result[k === oldKey ? newKey : k] = v;
    }
    return result;
  }

  // e.g. ('FR', '69') → 'FR-69', ('FR', '') → 'FR'
  const buildGroupKey = (country: string, postalCode: string): string => postalCode ? `${country}-${postalCode}` : country;

  // e.g. 'FR-69' → { country: 'FR', postalCode: '69' }
  const parseGroupKey = (key: string): { country: string; postalCode: string } => {
    const dashIndex = key.indexOf('-');
    if (dashIndex === -1) return { country: key, postalCode: '' };
    return { country: key.slice(0, dashIndex), postalCode: key.slice(dashIndex + 1) };
  };


  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          {t('Destination Settings (Preferred Destinations Mode Only)')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('Configure destination preferences for Auto Search Mode - Preferred Destinations. These destinations are used to SCORE offers that pass through or end at your preferred locations. The scores you set here affect how offers are ranked, not which offers are searched. Higher scores give better ranking to offers involving these destinations. This tab is only used when Preferred Destinations mode is selected, not for Circle Trip mode.')}
        </Typography>
      </Box>

      {renderGroupedSection(
        'preferredDestinations',
        t('Preferred Destinations'),
        'Destinations where you prefer to pick up or deliver loads. When offers are found that involve these destinations, they receive <strong>bonus points in scoring</strong> based on the score you configure. Higher scores = better ranking for offers involving these locations. <em>Note: This does not filter which offers are searched, only how they are scored.</em>',
        '#4caf50',
        'preferred-destinations'
      )}

      {renderGroupedSection(
        'returnDestinations',
        t('Return Destinations'),
        'Destinations used when <strong>Search offers back to registration country</strong> is activated in Preferred Destinations mode. These help find return routes back to your base location.',
        'border.blue',
        'return-destinations'
      )}

      <Accordion defaultExpanded sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('Destinations To Exclude')}</Typography>
            <CountChip count={destinationsToExclude.length} label="exclusion" color="background.red" />
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('Specify destinations that should be excluded when using <strong>Preferred Destinations mode</strong>. Searches will automatically skip offers to these locations.')}
          </Typography>

          {destinationsToExclude.map((item, index) => (
            <ExclusionRow
              key={index}
              destination={item}
              onCountryChange={(code) => handleUpdateExclusion(index, { country: code })}
              onCodesChange={(codes) => handleUpdateExclusion(index, { codes })}
              onRemove={() => handleRemoveExclusion(index)}
            />
          ))}

          <Button variant="outlined" startIcon={<AddIcon />} disableRipple onClick={handleAddExclusion} sx={OUTLINED_BUTTON_SX}>
            {t('Add Exclusion')}
          </Button>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default DestinationsTab;
